import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingCart, Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";

const Navbar = () => {
  const storeName = import.meta.env.VITE_STORE_NAME || "QYISSA";
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const { totalItems } = useCart();
  const location = useLocation();

  useEffect(() => {
    const controlNavbar = () => {
      if (typeof window !== 'undefined') {
        if (window.scrollY > lastScrollY && window.scrollY > 100) {
          // Scrolling down
          setIsVisible(false);
        } else {
          // Scrolling up
          setIsVisible(true);
        }
        setLastScrollY(window.scrollY);
      }
    };

    window.addEventListener('scroll', controlNavbar);
    return () => {
      window.removeEventListener('scroll', controlNavbar);
    };
  }, [lastScrollY]);

  const NavLinks = ({ onClick }: { onClick?: () => void }) => {
    const getLinkClass = (path: string) => {
      const isActive = location.pathname === path;
      return `text-foreground hover:opacity-75 transition-all duration-300 font-body font-light text-[10px] md:text-xs tracking-[0.25em] px-3 md:px-4 py-2 border ${
        isActive ? "border-foreground" : "border-transparent"
      }`;
    };

    return (
      <>
        <Link to="/" onClick={onClick} className={getLinkClass("/")}>HOME</Link>
        <Link to="/products" onClick={onClick} className={getLinkClass("/products")}>PRODUCTS</Link>
        <Link to="/about" onClick={onClick} className={getLinkClass("/about")}>OUR STORY</Link>
      </>
    );
  };

  return (
    <header className={`sticky top-0 z-50 w-full flex flex-col transition-transform duration-300 ${isVisible ? 'translate-y-0' : '-translate-y-full'}`}>
      {/* Main Navbar */}
      <nav className="bg-[#FAF8F5] border-b border-border/60">
        <div className="container px-4 md:px-8 flex items-center h-16 relative">
          {/* Mobile Hamburger (Left) */}
          <div className="md:hidden absolute left-4">
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <button className="w-12 h-12 -ml-3 flex items-center justify-center text-foreground hover:text-foreground/75" aria-label="Open menu">
                  <Menu size={20} strokeWidth={1.5} />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[80vw] sm:w-[350px] bg-[#FAF8F5] pt-16 rounded-none border-r border-border">
                <div className="flex flex-col gap-8 pl-4">
                  <NavLinks onClick={() => setIsOpen(false)} />
                  <Link to="/checkout" onClick={() => setIsOpen(false)} className="mt-8 inline-flex items-center justify-center bg-foreground text-background px-6 py-3 text-xs font-light tracking-[0.2em] uppercase hover:bg-foreground/90 transition-colors w-full max-w-[200px]">
                    VIEW BAG ({totalItems})
                  </Link>
                </div>
              </SheetContent>
            </Sheet>
          </div>

          {/* Logo (Centered on mobile, Left on desktop) */}
          <Link to="/" aria-label={`${storeName} Home`} className="flex items-center justify-center w-full md:w-auto md:justify-start group">
            <img src="/logo.png" alt={storeName} className="h-10 md:h-12 object-contain transition-opacity hover:opacity-75" />

          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-12 ml-16">
            <NavLinks />
          </div>

          {/* Desktop Right - Cart */}
          <div className="hidden md:flex items-center gap-4 ml-auto">
            <Link to="/checkout" className="text-xs font-body font-light uppercase tracking-[0.25em] text-foreground hover:opacity-75 transition-opacity flex items-center gap-2">
              BAG ({totalItems})
            </Link>
          </div>

          {/* Mobile Cart Icon (Right) */}
          <div className="md:hidden absolute right-4">
            <Link to="/checkout" aria-label={`Shopping Bag, ${totalItems} items`} className="text-foreground hover:text-foreground/75 text-xs font-body font-light tracking-[0.2em] uppercase">
              BAG ({totalItems})
            </Link>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
